/**
 * Supabase REST client for public-facing pages — SportsmediaIntel.com
 * Uses the anon key (read-only public data only).
 */

const SUPABASE_URL = process.env.NEXT_PUBLIC_SUPABASE_URL || '';
const SUPABASE_ANON_KEY = process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY || '';
const SUPABASE_SCHEMA = process.env.NEXT_PUBLIC_SUPABASE_SCHEMA || 'smi';
const SITE_ID = process.env.SITE_ID || 'sportsmediaintel';

export const SITE = SITE_ID;
export const SITE_URL = process.env.NEXT_PUBLIC_SITE_URL || 'https://sportsmediaintel.com';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface Article {
  id: string;
  site: string;
  title: string;
  slug: string;
  body: string;
  category: string;
  author: string;
  source_url?: string;
  meta_title?: string;
  seo_description?: string;
  seo_title?: string;
  featured_image?: string;
  status: string;
  published_at: string;
  created_at: string;
  updated_at: string;
}

export interface ForumBoard {
  id: string;
  site: string;
  slug: string;
  name: string;
  description?: string;
  category?: string;
  thread_count: number;
  post_count: number;
  last_activity_at?: string;
  created_at: string;
}

export interface ForumThread {
  id: string;
  site: string;
  board_id: string;
  board_slug?: string;
  title: string;
  slug: string;
  body: string;
  author_name: string;
  author_type: string;
  reply_count: number;
  view_count: number;
  is_pinned: boolean;
  is_locked: boolean;
  last_reply_at?: string;
  created_at: string;
}

export interface ForumPost {
  id: string;
  thread_id: string;
  author_name: string;
  author_type: string;
  body: string;
  created_at: string;
}

export interface EmailSubscriber {
  id: string;
  site: string;
  email: string;
  status: string;
  created_at: string;
}

// ─── Supabase REST Helper ─────────────────────────────────────────────────────

export async function supabaseFetch<T = unknown>(
  path: string,
  options: RequestInit = {}
): Promise<{ data: T | null; error: string | null }> {
  if (!SUPABASE_URL || !SUPABASE_ANON_KEY) {
    return { data: null, error: 'Supabase not configured' };
  }
  try {
    const res = await fetch(`${SUPABASE_URL}/rest/v1${path}`, {
      ...options,
      headers: {
        apikey: SUPABASE_ANON_KEY,
        Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
        'Content-Type': 'application/json',
        'Accept-Profile': SUPABASE_SCHEMA,
        'Content-Profile': SUPABASE_SCHEMA,
        ...(options.headers || {}),
      },
      next: { revalidate: 60 },
    });
    if (!res.ok) {
      const errText = await res.text().catch(() => res.statusText);
      return { data: null, error: errText };
    }
    const data = await res.json().catch(() => null);
    return { data: data as T, error: null };
  } catch (err) {
    return { data: null, error: err instanceof Error ? err.message : 'Fetch failed' };
  }
}

// ─── Article Queries ──────────────────────────────────────────────────────────

export async function getLatestArticles(limit = 20): Promise<Article[]> {
  const { data } = await supabaseFetch<Article[]>(
    `/articles?status=eq.published&order=published_at.desc&limit=${limit}&select=id,title,slug,category,author,seo_description,featured_image,published_at`
  );
  return data || [];
}

export async function getArticlesByCategory(category: string, limit = 20, offset = 0): Promise<Article[]> {
  const { data } = await supabaseFetch<Article[]>(
    `/articles?status=eq.published&category=eq.${category}&order=published_at.desc&limit=${limit}&offset=${offset}&select=id,title,slug,category,author,seo_description,featured_image,published_at`
  );
  return data || [];
}

export async function getArticleBySlug(slug: string): Promise<Article | null> {
  const { data } = await supabaseFetch<Article[]>(
    `/articles?status=eq.published&slug=eq.${encodeURIComponent(slug)}&limit=1&select=*`
  );
  return data?.[0] || null;
}

export async function getRelatedArticles(category: string, excludeSlug: string, limit = 4): Promise<Article[]> {
  const { data } = await supabaseFetch<Article[]>(
    `/articles?status=eq.published&category=eq.${category}&slug=neq.${encodeURIComponent(excludeSlug)}&order=published_at.desc&limit=${limit}&select=id,title,slug,category,author,seo_description,featured_image,published_at`
  );
  return data || [];
}

export async function searchArticles(query: string, limit = 20): Promise<Article[]> {
  const encoded = encodeURIComponent(query);
  const { data } = await supabaseFetch<Article[]>(
    `/articles?status=eq.published&or=(title.ilike.*${encoded}*,seo_description.ilike.*${encoded}*)&order=published_at.desc&limit=${limit}&select=id,title,slug,category,author,seo_description,featured_image,published_at`
  );
  return data || [];
}

export async function getAllPublishedArticles(): Promise<Article[]> {
  const { data } = await supabaseFetch<Article[]>(
    `/articles?status=eq.published&order=published_at.desc&select=title,slug,category,published_at,updated_at`
  );
  return data || [];
}

// ─── Forum Queries ────────────────────────────────────────────────────────────

export async function getForumBoards(): Promise<ForumBoard[]> {
  const { data } = await supabaseFetch<ForumBoard[]>(
    `/forum_categories?site=eq.${SITE_ID}&order=name.asc&select=*`
  );
  return data || [];
}

export async function getForumBoard(slug: string): Promise<ForumBoard | null> {
  const { data } = await supabaseFetch<ForumBoard[]>(
    `/forum_categories?site=eq.${SITE_ID}&slug=eq.${slug}&limit=1&select=*`
  );
  return data?.[0] || null;
}

export async function getThreadsByBoard(boardId: string, limit = 25, offset = 0): Promise<ForumThread[]> {
  const { data } = await supabaseFetch<ForumThread[]>(
    `/forum_threads?site=eq.${SITE_ID}&board_id=eq.${boardId}&order=is_pinned.desc,last_reply_at.desc&limit=${limit}&offset=${offset}&select=*`
  );
  return data || [];
}

export async function getThreadBySlug(slug: string): Promise<ForumThread | null> {
  const { data } = await supabaseFetch<ForumThread[]>(
    `/forum_threads?site=eq.${SITE_ID}&slug=eq.${slug}&limit=1&select=*`
  );
  return data?.[0] || null;
}

export async function getPostsByThread(threadId: string): Promise<ForumPost[]> {
  const { data } = await supabaseFetch<ForumPost[]>(
    `/forum_replies?thread_id=eq.${threadId}&order=created_at.asc&select=*`
  );
  return data || [];
}

// ─── Newsletter Queries ───────────────────────────────────────────────────────

export async function subscribeEmail(email: string): Promise<{ success: boolean; error?: string }> {
  const { error } = await supabaseFetch('/newsletter_subscribers', {
    method: 'POST',
    body: JSON.stringify({
      email,
      status: 'active',
    }),
    headers: { Prefer: 'return=minimal' },
  });
  if (error) return { success: false, error };
  return { success: true };
}

// ─── Category Metadata ────────────────────────────────────────────────────────

export const CATEGORIES: Record<string, { label: string; description: string }> = {
  'live-production': { label: 'Live Production', description: 'Remote production, OB trucks, IP workflows, and mobile broadcast units.' },
  'streaming-ott': { label: 'Sports Streaming', description: 'OTT platforms, CDN, encoding, latency, and direct-to-consumer sports delivery.' },
  'cameras-imaging': { label: 'Cameras & Imaging', description: 'Broadcast cameras, PTZ, robotic systems, aerial, and player tracking.' },
  'switching-graphics': { label: 'Switching & Graphics', description: 'Production switchers, video routers, AR overlays, score bugs, and telestrators.' },
  'virtual-sets-xr': { label: 'Virtual Sets & XR', description: 'LED volumes, XR stages, virtual studios, and real-time rendering.' },
  'transmission-rf': { label: 'Transmission & RF', description: 'ATSC 3.0, satellite, fiber, bonded cellular, 5G broadcast, and tower infrastructure.' },
  'audio': { label: 'Audio', description: 'Parabolic mics, intercom, AoIP, commentary, and immersive audio for sports.' },
  'replay-post': { label: 'Replay & Post', description: 'Super slow-motion replay, EVS, MAM, AI editing, and highlight clip systems.' },
  'mobile-venue-tech': { label: 'Mobile & Venue Tech', description: 'Stadium LED boards, jumbotrons, in-venue displays, and broadcast infrastructure.' },
  'ai-automation': { label: 'AI & Automation', description: 'AI cameras, automated production, computer vision, and real-time stats.' },
  'rights-deals': { label: 'Rights & Deals', description: 'Media rights deals, RSNs, streaming rights, and sports network contracts.' },
  'executive-suite': { label: 'Executive Suite', description: 'Executive appointments, promotions, industry awards, and career moves.' },
  'remote-cloud': { label: 'Remote & Cloud', description: 'REMI workflows, at-home production, cloud-based infrastructure.' },
  'broadcast-tech': { label: 'Broadcast Tech', description: 'IP production, HDR, 4K UHD pipelines, and signal distribution.' },
  'nab-show': { label: 'NAB Show', description: 'Official NAB Show news, product launches, and coverage from Las Vegas.' },
  'ibc-show': { label: 'IBC Show', description: 'Official IBC Show news, product launches, and coverage from Amsterdam.' },
  'svg-summit': { label: 'SVG Summit', description: 'Sports Video Group Summit coverage, panels, and industry insights.' },
  'product-news': { label: 'Product News', description: 'New product launches, announcements, and press releases from vendors.' },
  'industry-events': { label: 'Industry Events', description: 'Trade shows, conferences, and industry event coverage.' },
  'press-room': { label: 'Press Room', description: 'Official press releases and company announcements.' },
};

function penNameToSlug(penName: string): string {
  return penName
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-');
}

export { penNameToSlug };

export async function getArticlesByPenName(penName: string | null, limit = 20): Promise<Article[]> {
  if (!penName) return [];
  const { data } = await supabaseFetch<Article[]>(
    `/articles?status=eq.published&author=eq.${encodeURIComponent(penName)}&order=published_at.desc&limit=${limit}&select=id,title,slug,category,author,seo_description,featured_image,published_at`
  );
  return data || [];
}

export async function getAuthorPenNames(): Promise<string[]> {
  const { data } = await supabaseFetch<{ author: string }[]>(
    `/articles?site=eq.${SITE_ID}&status=eq.published&select=author`
  );
  if (!data) return [];
  const unique = Array.from(new Set(data.map((r) => r.author).filter(Boolean)));
  return unique;
}

export function slugToPenName(slug: string): string | null {
  const decoded = decodeURIComponent(slug);
  return decoded
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}